// SimulatorContext.js — State management for the simulation facility.
// Manages params, results, loading, and run history.
// Persists session_id in sessionStorage for anonymous runs.

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import api from '../services/api';

const SimulatorContext = createContext(null);

export function useSimulator() {
  const ctx = useContext(SimulatorContext);
  if (!ctx) throw new Error('useSimulator must be used within SimulatorProvider');
  return ctx;
}

function getSessionId() {
  let id = sessionStorage.getItem('sim_session_id');
  if (!id) {
    id = 'sess_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem('sim_session_id', id);
  }
  return id;
}

export function SimulatorProvider({ children }) {
  const [params, setParams] = useState({
    sourceCurrency: 'USD',
    destinationCountry: 'Kenya',
    instrument: 'tbill',
    tenorDays: 91,
    investmentAmount: 100000,
    forwardMode: 'none',
    customPremiumBps: 0,
    rolloverMode: 'off',
    startDate: '2024-01-01',
    endDate: '2024-12-31',
  });

  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [runHistory, setRunHistory] = useState([]);
  const [activeRun, setActiveRun] = useState(null);
  const [mode, setMode] = useState('historical'); // historical | live

  // Rate data state
  const [rateData, setRateData] = useState(null);
  const [config, setConfig] = useState(null);

  const sessionId = getSessionId();

  // ─── Pre-fill from URL search params (e.g. from case-study links) ───
  useEffect(() => {
    const url = new URL(window.location.href);
    const sp = url.searchParams;
    if (!sp.has('source') && !sp.has('market') && !sp.has('tenor') && !sp.has('amount') && !sp.has('hedge') && !sp.has('mode')) return;

    setParams(prev => {
      const next = { ...prev };
      if (sp.has('source'))  next.sourceCurrency     = sp.get('source');
      if (sp.has('market'))  next.destinationCountry  = sp.get('market');
      if (sp.has('tenor'))   next.tenorDays           = Number(sp.get('tenor'));
      if (sp.has('amount'))  next.investmentAmount     = Number(sp.get('amount'));
      if (sp.has('hedge'))   next.forwardMode          = sp.get('hedge');
      return next;
    });
    if (sp.has('mode') && sp.get('mode') === 'live') setMode('live');

    // Clear URL params so they don't persist on refresh
    window.history.replaceState({}, '', url.pathname);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const runSimulation = useCallback(async (overrideParams) => {
    const simParams = overrideParams || params;
    setLoading(true);
    setError(null);
    setResults(null);

    try {
      const response = await api.post('/simulator/investor/run', {
        ...simParams,
        sessionId,
      });

      if (response.success) {
        setResults(response.data);
        setActiveRun(response.data.summary);
        // Add to local history
        setRunHistory(prev => [response.data.summary, ...prev].slice(0, 50));
      } else {
        setError(response.error || 'Simulation failed');
      }
    } catch (err) {
      setError(err.message || 'Simulation failed');
    } finally {
      setLoading(false);
    }
  }, [params, sessionId]);

  const loadHistory = useCallback(async () => {
    try {
      const response = await api.get(`/simulator/investor/runs?sessionId=${sessionId}&mode=${mode}`);
      if (response.success) {
        setRunHistory(response.data);
      }
    } catch (err) {
      console.warn('Failed to load run history:', err.message);
    }
  }, [sessionId, mode]);

  const loadRun = useCallback(async (runId) => {
    setLoading(true);
    try {
      const response = await api.get(`/simulator/investor/runs/${runId}`);
      if (response.success) {
        setResults({
          summary: response.data.run,
          cashflows: response.data.cashflows,
        });
        setActiveRun(response.data.run);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRateData = useCallback(async () => {
    try {
      const [rateResponse, configResponse] = await Promise.all([
        api.get('/simulator/rate-data'),
        api.get('/simulator/config'),
      ]);
      if (rateResponse.success) setRateData(rateResponse.data);
      if (configResponse.success) setConfig(configResponse.data);
    } catch (err) {
      console.warn('Failed to load rate data:', err.message);
    }
  }, []);

  // ─── Live mode ──────────────────────────────────────────────────────
  const [liveRuns, setLiveRuns] = useState([]);
  const [activeLiveRun, setActiveLiveRun] = useState(null);
  const [liveRunDetail, setLiveRunDetail] = useState(null);

  const createLiveRun = useCallback(async (overrideParams) => {
    const simParams = overrideParams || params;
    setLoading(true);
    setError(null);

    try {
      const response = await api.createLiveInvestorRun({
        ...simParams,
        endDate: simParams.endDate,
      });

      if (response.success) {
        setActiveLiveRun(response.data);
        setLiveRuns(prev => [response.data, ...prev]);
        return response.data;
      } else {
        setError(response.error || 'Failed to create live run');
      }
    } catch (err) {
      setError(err.message || 'Failed to create live run');
    } finally {
      setLoading(false);
    }
  }, [params]);

  const loadLiveRuns = useCallback(async () => {
    try {
      const response = await api.getLiveInvestorRuns();
      if (response.success) {
        setLiveRuns(response.data);
      }
    } catch (err) {
      console.warn('Failed to load live runs:', err.message);
    }
  }, []);

  const loadLiveRunDetail = useCallback(async (runId) => {
    setLoading(true);
    try {
      const response = await api.getLiveInvestorRun(runId);
      if (response.success) {
        setLiveRunDetail(response.data);
        setActiveLiveRun(response.data.run);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const cancelLiveRun = useCallback(async (runId) => {
    try {
      const response = await api.cancelLiveInvestorRun(runId);
      if (response.success) {
        setLiveRuns(prev => prev.map(r => r.id === runId ? { ...r, status: 'cancelled' } : r));
        if (activeLiveRun && activeLiveRun.id === runId) {
          setActiveLiveRun(prev => ({ ...prev, status: 'cancelled' }));
        }
      }
    } catch (err) {
      setError(err.message);
    }
  }, [activeLiveRun]);

  const clearResults = useCallback(() => {
    setResults(null);
    setActiveRun(null);
    setActiveLiveRun(null);
    setLiveRunDetail(null);
    setError(null);
  }, []);

  const value = {
    params, setParams,
    results, loading, error,
    runHistory, activeRun,
    mode, setMode,
    rateData, config,
    runSimulation, loadHistory, loadRun, loadRateData, clearResults,
    sessionId,
    // Live mode
    liveRuns, activeLiveRun, liveRunDetail,
    createLiveRun, loadLiveRuns, loadLiveRunDetail, cancelLiveRun,
  };

  return (
    <SimulatorContext.Provider value={value}>
      {children}
    </SimulatorContext.Provider>
  );
}
